/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react';
import { Button, Form, FormControl, InputGroup, Tab, Tabs } from 'react-bootstrap';
import { FormattedMessage, injectIntl } from 'react-intl';
import * as operatorApi from '../../api/CollectionpointOperators';
import Modal from '../../components/Modal';
import { ICollectionPoint, ICollectionPointOperator } from '../../util/DataModelInterface';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CollectionPointList from './CollectionPointList';
import OperatorList from './OperatorList';
import { ReportRow, exportCompareSummaryReport } from '../../api/SummaryReport';

interface IManageCollectionpointProps {
  intl: any;
}

const ManageCollectionpoints: React.FunctionComponent<IManageCollectionpointProps> = (props: IManageCollectionpointProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [readyOpen, setReadyOpen] = useState(false);
  const [operators, setOperators] = useState([] as ICollectionPointOperator[]);
  const [stores, setStores] = useState([] as ICollectionPointOperator[]);
  const [points, setPoints] = useState([] as ICollectionPoint[]);
  const [fOperators, setFOperators] = useState([] as ICollectionPointOperator[]);
  const [fStores, setFStores] = useState([] as ICollectionPointOperator[]);
  const [operatorName, setOperatorName] = useState('');
  const [storeName, setStoreName] = useState('');
  const [, setTab] = useState("operators");
  const [storeList, setStoreList] = useState(true);

  useEffect(() => {
    updateData(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateData = (force: boolean) => {
    operatorApi.getCollectionpointOperators(force).then((data: ICollectionPointOperator[]) => {
      if (data) {
        data.sort(function(a, b){ // Alphabetical order in operator and store collection tabs
          if(a.name < b.name) { return -1; }
          if(a.name > b.name) { return 1; }
          return 0;
        });
        let os = data.filter((o) => o.reportformat !== 'STORE');
        let ss = data.filter((o) => o.reportformat === 'STORE');
        if(operators!==os){
          setOperators(os);
          setFOperators(filterRows(operatorName, os));
        }
        if(stores!==ss) {
          setStores(ss);
          setFStores(filterRows(storeName, ss));
        }
      }
      operatorApi.getAllCollectionpointInfo(force).then((data: ICollectionPoint[]) => {
        if (data) {
          data.sort(function(a, b){ // Id order in collectionpoint tab
            if(a.info.collectionpoint_id < b.info.collectionpoint_id) { return -1; }
            if(a.info.collectionpoint_id > b.info.collectionpoint_id) { return 1; }
            return 0;
          });
          if(points!==data)
            setPoints(data);
        }
      });
      if(force) setReadyOpen(true);
    }).finally(() => {
      setModalOpen(false);
    });
  };

  const getModal = () =>
    <Modal
      onClose={() => { setModalOpen(false); }}
      closeOnClickOutside={false}
      width={400}
      height={250}>
      <div className="ModalView">
        <div className="spinner"/>
        <div className="message"><FormattedMessage id="settings.operators.saving"/></div>
      </div>
    </Modal>

  const getErrorModal = () =>
    <Modal
      onClose={() => setErrorOpen(false)}
      closeOnClickOutside={true}
      width={400}
      height={200}>
      <div className="ModalView">
        <FontAwesomeIcon className="checkmark" icon="exclamation-triangle" color="var(--serty-orange)" size="5x"/>
        <div className="message">
          <FormattedMessage id="settings.operators.saving.error"/>
        </div>
        <div className="errorText">
          {errorMsg}
        </div>
      </div>
    </Modal>

  const getReadyModal = () =>
    <Modal
      onClose={() => setReadyOpen(false)}
      closeOnClickOutside={true}
      width={400}
      height={200}
      autohide={true}>
      <div className="ModalView">
        <div className="checkmark">
          <FontAwesomeIcon icon="check-circle" color="var(--serty-green)" size="5x" />
        </div>
        <div className="message"><FormattedMessage id="settings.operators.saving.ready"/></div>
      </div>
    </Modal>

  const filterByOperatorName = (value: string) => {
    setOperatorName(value);
  }

  const filterByStoreName = (value: string) => {
    setStoreName(value);
  }

  const filterRows = (name: string, rows: ICollectionPointOperator[]) => {
    const nameFilter = (row: ICollectionPointOperator) => {
      if (name === '' || row.name.toLowerCase().includes(name.toLowerCase()) ||
          row.collection_points.find(r => r.info.name.toLowerCase().includes(name.toLowerCase()))) {
        return true;
      }
      return false;
    }
    let data: ICollectionPointOperator[] = [];
    if (rows && Array.isArray(rows)) {
      data = rows.filter(nameFilter);
    }
    return data;
  }

  const filter = (isStores: boolean, ops: ICollectionPointOperator[]) => {
    isStores ? setFStores(filterRows(storeName, ops)) : setFOperators(filterRows(operatorName, ops));
  }

  const clearFilter = (isStores: boolean) => {
    isStores ? filterByStoreName('') : filterByOperatorName('');
  }

  const operatorListTab = (isStores = false) => {
    let fops = isStores ? fStores : fOperators; // Filtered
    let ops = isStores ? stores : operators; // All
    let name = isStores ? storeName : operatorName; // Searched name
    return(
    <Tab eventKey={ isStores ? "store-operators" : "operators"} title={props.intl.formatMessage({id: isStores ? 'settings.operators.stores' : 'settings.operators'})}>
      <InputGroup className="search-group d-flex flex-md-row flex-column mb-3">
        <FormControl className="form-group"
          placeholder={props.intl.formatMessage({id: 'settings.operators.collectionpoints.findbyname'})}
          value={name}
          onKeyDown={(target:any) => {
            if(target.keyCode===13) filter(isStores, ops); // Filter with Enter key
            else if (target.keyCode===27) clearFilter(isStores); // Clear with Esc key
          }}
          onChange={(event) => isStores ? filterByStoreName(event.target.value) : filterByOperatorName(event.target.value)}
        />
        <Button className="ml-md-auto" variant="outline-secondary" id="button-addon2"
                onClick={() => clearFilter(isStores)}>
          <FontAwesomeIcon icon="times"/>
        </Button>
        <button className="btn btn-link ml-md-auto" 
                onClick={() => filter(isStores, ops)}>
          <FontAwesomeIcon icon="search"/>
        </button>
      </InputGroup>
      <OperatorList operators={fops}
                    searching={fops.length!==ops.length && ops.length > 0}
                    points={points}
                    stores={isStores}
                    update={ () : void => { updateData(true); }}
                    showModal={ () : void => { setModalOpen(true); }}
                    onError={ (msg) : void => { setErrorMsg(msg); setErrorOpen(true); setModalOpen(false); }}/>
    </Tab>);
  }

  const formatEmailsForExport = () => {
    let formatted = [] as ReportRow[];
    let row = {} as ReportRow;
    let value = '';
    fOperators.forEach((d: ICollectionPointOperator) => {
      value += (d.emails + ';');
    });
    fStores.forEach((d: ICollectionPointOperator) => {
      value += (d.emails + ';');
    });
    let key = props.intl.formatMessage({id:`settings.operators`});
    if(storeList) key += ' & ' + props.intl.formatMessage({id:`settings.operators.stores`});
    row[key] = value;
    formatted.push(row);
    console.log(formatted);
    return formatted;
  }

  return (
    <div>
      { modalOpen && getModal() }
      { errorOpen && getErrorModal() }
      { readyOpen && getReadyModal() }
      <h1>
        <FormattedMessage id="settings.manage.header"/>
        -
        <FormattedMessage id="settings.collectionpoints.header"/>
      </h1>

      <Tabs className="MainForm" defaultActiveKey="operators" id="manage-collectionpoints-tabs" onSelect={(eventKey: any) => setTab(eventKey ? eventKey : "operators")}>
        {operatorListTab()}
        {operatorListTab(true)}
        <Tab eventKey="points" title={props.intl.formatMessage({id:'settings.operators.collectionpoints'})}>
          <CollectionPointList points={points}/>
        </Tab>
        <Tab eventKey="mailinglist" title={props.intl.formatMessage({id:'settings.operators.mailinglist'})}>
            <p className="tip">
              <FontAwesomeIcon className="tipIcon" icon={"info-circle" }/>
              <FormattedMessage id="settings.operators.mailinglist.tip"/>
            </p>
            <div className="d-flex flex-row">
            <button
                className={"btn btn-primary"}
                onClick={() => exportCompareSummaryReport('mailinglist',
                              { header: props.intl.formatMessage({id: 'settings.operators.mailinglist'}),
                                data: formatEmailsForExport(),
                                compareHeader: "", compareData: {}})}>
                { props.intl.formatMessage({id: 'settings.operators.export.mailinglist'}) }
            </button>
              <Form.Check type="checkbox" className="d-flex align-items-center ml-md-4">
                <Form.Check.Label className='form-check-label-padded'>{props.intl.formatMessage({id: 'settings.operators.stores'})}</Form.Check.Label>
                <Form.Check.Input type="checkbox" isValid checked={storeList} 
                                  onChange={(e: any) => { setStoreList(!storeList)}}/>
              </Form.Check>
          </div>
        </Tab>
      </Tabs>
    </div>
  );
}

export default injectIntl(ManageCollectionpoints);
